/**
 * Default Marketing Configuration Templates
 *
 * Pre-configured defaults for marketing automation that engineers
 * can use as starting points. Includes lead scoring, automation
 * triggers, and lead magnet configurations tailored for private
 * lending businesses.
 *
 * @see Issue #638 - Marketing Automation Tools
 */

import type { AutomationConfig, LeadMagnetConfig, LeadScoringConfig } from '../types/marketing.types';
import { LeadScoringTier } from '../types/marketing.types';

// =============================================================================
// DEFAULT LEAD SCORING CONFIG
// =============================================================================

/**
 * Default lead scoring configuration with lending-specific factors.
 *
 * Four balanced categories (25 points each, 100 total):
 * - Property Data: property type, location, value range, condition
 * - Borrower Profile: experience, entity type, credit score, repeat status
 * - Deal Viability: loan amount, LTV, exit strategy, timeline
 * - Engagement: website visits, calculator usage, application activity, email, response time
 *
 * Tier thresholds:
 * - Hot: 75+ (auto-assign to LO, notify team)
 * - Warm: 50+ (notify team)
 * - Cool: 25+ (no notifications)
 * - Cold: 0+ (no notifications)
 */
export const DEFAULT_LEAD_SCORING_CONFIG: LeadScoringConfig = {
  factors: {
    propertyData: {
      maxPoints: 25,
      subFactors: [
        {
          name: 'Property Type Match',
          points: 10,
          description: 'Property type aligns with lender programs (e.g., SFR, multi-family, commercial)',
        },
        {
          name: 'Location in Target Market',
          points: 5,
          description: 'Property is located in a state or metro area the lender actively serves',
        },
        {
          name: 'Estimated Value in Range',
          points: 5,
          description: 'After-repair or as-is value falls within the lender pricing sweet spot',
        },
        {
          name: 'Property Condition',
          points: 5,
          description: 'Condition level (stabilized, light rehab, heavy rehab) matches available programs',
        },
      ],
    },

    borrowerProfile: {
      maxPoints: 25,
      subFactors: [
        {
          name: 'Experience Level',
          points: 10,
          description: 'Number of completed real estate transactions (0, 1-3, 4-10, 10+) indicating investor maturity',
        },
        {
          name: 'Entity Type',
          points: 5,
          description: 'Borrowing entity structure (LLC, corp, individual) that meets lender requirements',
        },
        {
          name: 'Credit Score Range',
          points: 5,
          description: 'FICO score falls within minimum program threshold (e.g., 620+, 660+, 700+)',
        },
        {
          name: 'Repeat Borrower',
          points: 5,
          description: 'Borrower has previously funded a loan with this lender',
        },
      ],
    },

    dealViability: {
      maxPoints: 25,
      subFactors: [
        {
          name: 'Loan Amount in Range',
          points: 10,
          description: 'Requested loan amount is within the lender minimum and maximum program limits',
        },
        {
          name: 'LTV Within Guidelines',
          points: 5,
          description: 'Loan-to-value ratio meets underwriting guidelines (e.g., max 75% ARV, 90% LTC)',
        },
        {
          name: 'Exit Strategy Clarity',
          points: 5,
          description: 'Borrower has a clear, documented exit strategy (sale, refinance, payoff)',
        },
        {
          name: 'Timeline Urgency',
          points: 5,
          description: 'Deal has a defined closing deadline indicating motivated action (e.g., under contract)',
        },
      ],
    },

    engagement: {
      maxPoints: 25,
      subFactors: [
        {
          name: 'Website Visits',
          points: 3,
          description: 'Lead has visited the lender website multiple times indicating interest',
        },
        {
          name: 'Calculator Used',
          points: 5,
          description: 'Lead used the loan calculator to estimate payments, indicating active deal evaluation',
        },
        {
          name: 'Application Started',
          points: 7,
          description: 'Lead began the loan intake form, indicating high intent to borrow',
        },
        {
          name: 'Email Engagement',
          points: 5,
          description: 'Lead opened or clicked links in marketing or nurture emails',
        },
        {
          name: 'Response Time',
          points: 5,
          description: 'Lead responded to outreach within 24 hours, indicating availability and interest',
        },
      ],
    },
  },

  tiers: [
    {
      tier: LeadScoringTier.HOT,
      minScore: 75,
      label: 'Hot Lead',
      autoAssign: true,
      notifyTeam: true,
    },
    {
      tier: LeadScoringTier.WARM,
      minScore: 50,
      label: 'Warm Lead',
      autoAssign: false,
      notifyTeam: true,
    },
    {
      tier: LeadScoringTier.COOL,
      minScore: 25,
      label: 'Cool Lead',
      autoAssign: false,
      notifyTeam: false,
    },
    {
      tier: LeadScoringTier.COLD,
      minScore: 0,
      label: 'Cold Lead',
      autoAssign: false,
      notifyTeam: false,
    },
  ],
};

// =============================================================================
// DEFAULT AUTOMATION CONFIG
// =============================================================================

/**
 * Default automation configuration with common marketing triggers.
 *
 * Pre-configured trigger rules:
 * 1. calculator.used -> enroll in new-lead-nurture sequence
 * 2. intake.abandoned -> enroll in application-abandoned (4h delay)
 * 3. loan.funded -> enroll in referral-ask (30-day delay)
 * 4. lead.score_changed to hot -> notify team immediately
 * 5. investor.inquiry_created -> enroll in investor-inquiry sequence
 */
export const DEFAULT_AUTOMATION_CONFIG: AutomationConfig = {
  triggers: [
    {
      event: 'calculator.used',
      action: 'enroll_sequence',
      sequence: 'seq-mkt-new-lead-nurture',
      conditions: {},
    },
    {
      event: 'intake.abandoned',
      action: 'enroll_sequence',
      sequence: 'seq-mkt-application-abandoned',
      conditions: {},
      delay: 240, // 4 hours in minutes
    },
    {
      event: 'loan.funded',
      action: 'enroll_sequence',
      sequence: 'seq-mkt-referral-ask',
      conditions: {},
      delay: 43200, // 30 days in minutes
    },
    {
      event: 'lead.score_changed',
      action: 'notify_team',
      conditions: { newTier: 'HOT' },
      notification: {
        channel: 'in_app',
        recipients: ['sales-team'],
        messageTemplate:
          'Hot lead alert: {{lead_name}} just reached a score of {{score}} points. Tier: HOT. Review and reach out ASAP.',
      },
    },
    {
      event: 'investor.inquiry_created',
      action: 'enroll_sequence',
      sequence: 'seq-mkt-investor-inquiry',
      conditions: {},
    },
  ],
};

// =============================================================================
// DEFAULT LEAD MAGNETS
// =============================================================================

/**
 * Default lead magnet configurations for private lender websites.
 *
 * Five pre-configured lead magnets covering common lending content:
 * 1. Loan program guide (auto-generated from current rate sheets)
 * 2. Rehab budget template (static Excel spreadsheet)
 * 3. Market report (AI-generated from market data)
 * 4. Investment opportunity summary (auto-generated from fund data)
 * 5. First-time flipper checklist (static PDF)
 */
export const DEFAULT_LEAD_MAGNETS: LeadMagnetConfig[] = [
  {
    id: 'lm-loan-program-guide',
    name: 'Loan Program Guide',
    type: 'pdf',
    sourceType: 'auto_generated',
    dataSource: 'current_rate_sheets',
    description:
      'Comprehensive guide to all available loan programs including current rates, terms, LTV limits, and qualification requirements. Automatically generated from the latest rate sheet data.',
  },
  {
    id: 'lm-rehab-budget-template',
    name: 'Rehab Budget Template',
    type: 'excel',
    sourceType: 'static',
    description:
      'Professional rehab budget spreadsheet with line-item cost categories, draw schedule tracking, contingency calculations, and before/after valuation worksheets. Ready to use for fix-and-flip projects.',
  },
  {
    id: 'lm-market-report',
    name: 'Market Report',
    type: 'report',
    sourceType: 'ai_generated',
    dataSource: 'market_data_feed',
    description:
      'AI-generated market analysis report covering local real estate trends, median prices, days on market, rental yields, and investment opportunity zones for your target markets.',
  },
  {
    id: 'lm-investment-opportunity-summary',
    name: 'Investment Opportunity Summary',
    type: 'pdf',
    sourceType: 'auto_generated',
    dataSource: 'fund_data',
    description:
      'Current investment opportunity overview for accredited investors including target returns, fund structure, collateral breakdown, historical performance, and minimum investment thresholds.',
  },
  {
    id: 'lm-first-time-flipper-checklist',
    name: 'First-Time Flipper Checklist',
    type: 'pdf',
    sourceType: 'static',
    description:
      'Step-by-step checklist for first-time fix-and-flip investors covering property acquisition, financing, renovation planning, contractor management, and exit strategy execution.',
  },
];
